import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, RotateCcw, LogOut, Volume2, VolumeX } from 'lucide-react';
import { useRipple } from '../context/RippleContext';

// Character names
const heroMales = ['RHY', 'DJEMBE', 'XVXPARIS', 'OSHUA', 'GRAVE', 'PMXNK', 'JEFF', 'KWAZII', 'DEDE VALENTINO', 'M1H1', 'SWERVE', 'GOKU'];
const heroFemales = ['BETSY', 'KAYLA', 'LUCI', 'JADE', 'MIKAY', 'RYN', 'ZEZE', 'DAY', 'KARISSA', 'DIANDRA', 'LAURA', 'KYRA'];
const villains = ['AUTUMN', 'SUMMRS', 'KANKAN', 'FIJI', 'DHAMARI', 'DUSJINI', 'JD', 'BRENT RAMBO', 'ZAIRE', 'CEDES', 'MACTEN', 'CALEB'];

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Audio paths mapped to games
const gameAudioMap = {
  corbett: `${API_URL}/api/static/audio/track1.mp3`,
  laurel: `${API_URL}/api/static/audio/track2.mp3`,
  annapolis: `${API_URL}/api/static/audio/track3.mp3`,
  isabel: `${API_URL}/api/static/audio/track4.mp3`,
  quivira: `${API_URL}/api/static/audio/track5.mp3`,
};

// Game Result Modal with clear WIN/LOSE display
const GameResultModal = ({ isWin, gameNumber, onRedo, onExit }) => {
  const prizes = {
    1: { link: 'https://on.soundcloud.com/kp5A8dx8VFuWBZSZoa', text: 'SECRET LINK #1' },
    2: { link: 'https://on.soundcloud.com/nGPA9cLXFc12gN2SmC', text: 'SECRET LINK #2' },
    3: { link: 'https://on.soundcloud.com/PU46o2GcYqfcZpqhq8', text: 'SECRET LINK #3' },
    4: { link: 'https://on.soundcloud.com/8WmJk9Q5eYh2vNrC8', text: 'SECRET LINK #4' },
    5: { link: 'https://on.soundcloud.com/LpV7sK3dRfT9mXnY6', text: 'SECRET LINK #5' }
  };

  return (
    <div className="absolute inset-0 bg-black/95 flex items-center justify-center z-50 rounded-2xl">
      <div className="text-center p-8 max-w-sm">
        {isWin ? (
          <>
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-4xl font-heading text-green-400 mb-2">YOU WON!</h3>
            <p className="text-white/60 text-sm mb-6">Congratulations! Here's your reward:</p>
            <a 
              href={prizes[gameNumber].link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl mb-4 hover:scale-105 transition-transform text-lg"
            >
              🔓 {prizes[gameNumber].text}
            </a>
          </>
        ) : (
          <>
            <div className="text-6xl mb-4">💀</div>
            <h3 className="text-4xl font-heading text-red-400 mb-2">YOU LOST!</h3>
            <p className="text-white/60 text-sm mb-6">Better luck next time!</p>
          </>
        )}
        <div className="flex gap-4 justify-center mt-6">
          <button onClick={onRedo} className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl flex items-center gap-2 text-lg">
            <RotateCcw className="w-5 h-5" /> PLAY AGAIN
          </button>
          <button onClick={onExit} className="px-8 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl flex items-center gap-2 text-lg">
            <LogOut className="w-5 h-5" /> EXIT
          </button>
        </div>
      </div>
    </div>
  );
};

// Audio Hook for background music
const useGameAudio = (audioSrc, isPlaying) => {
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (!audioSrc) return;
    
    audioRef.current = new Audio(audioSrc);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.4;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioSrc]);

  useEffect(() => {
    if (!audioRef.current) return;
    
    if (isPlaying && !isMuted) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, isMuted]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      if (!isMuted) {
        audioRef.current.pause();
      } else if (isPlaying) {
        audioRef.current.play().catch(() => {});
      }
    }
  };

  return { isMuted, toggleMute };
};

// GAME 1: CORBETT - Memory Match (EASIER: 3x4 grid = 6 pairs, 20 moves to win)
const CorbettGame = ({ onClose }) => {
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const { isMuted, toggleMute } = useGameAudio(gameAudioMap.corbett, gameStarted && !gameOver);

  const initCards = () => {
    const symbols = ['🎵', '🎤', '🎸', '🥁', '🎹', '🎷'];
    const pairs = [...symbols, ...symbols].sort(() => Math.random() - 0.5).map((symbol, i) => ({ id: i, symbol }));
    setCards(pairs);
  };

  const startGame = (name) => {
    setPlayerName(name);
    setGameStarted(true);
    setMoves(0);
    setFlipped([]);
    setMatched([]);
    setGameOver(false);
    initCards();
  };

  const flipCard = (id) => {
    if (flipped.length === 2 || flipped.includes(id) || matched.includes(id) || gameOver) return;
    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);
    
    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [first, second] = newFlipped;
      if (cards[first].symbol === cards[second].symbol) {
        const newMatched = [...matched, first, second];
        setMatched(newMatched);
        setFlipped([]);
        
        // Win condition: all pairs matched
        if (newMatched.length === cards.length) {
          setTimeout(() => {
            setGameOver(true);
            setIsWin(true); // Always win if completed
          }, 300);
        }
      } else {
        setTimeout(() => setFlipped([]), 600);
      }
      
      // Lose condition: exceeded 20 moves without completing
      if (moves + 1 >= 20 && matched.length + 2 < cards.length) {
        setTimeout(() => {
          setGameOver(true);
          setIsWin(false);
        }, 700);
      }
    }
  };

  if (!gameStarted) {
    return (
      <div className="text-center p-4">
        <h3 className="text-2xl font-heading text-white mb-2">CORBETT</h3>
        <p className="text-white/60 text-sm mb-4">Match all 6 pairs within 20 moves to WIN!</p>
        <div className="flex flex-wrap gap-2 justify-center max-h-28 overflow-y-auto mb-4">
          {[...heroMales, ...heroFemales].map(name => (
            <button key={name} onClick={() => startGame(name)} className="px-3 py-2 bg-purple-600/50 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors font-medium">{name}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="text-white font-mono text-sm">Pairs: {matched.length / 2}/6 | Moves: {moves}/20</div>
        <button onClick={toggleMute} className="text-white/60 hover:text-white p-2">
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {cards.map((card, i) => (
          <button
            key={i}
            onClick={() => flipCard(i)}
            className={`aspect-square rounded-xl flex items-center justify-center text-3xl font-bold transition-all duration-300 ${
              flipped.includes(i) || matched.includes(i)
                ? 'bg-gradient-to-br from-purple-500 to-pink-500 scale-100'
                : 'bg-white/20 hover:bg-white/30 scale-95 hover:scale-100'
            }`}
          >
            {(flipped.includes(i) || matched.includes(i)) ? card.symbol : '?'}
          </button>
        ))}
      </div>
      {gameOver && <GameResultModal isWin={isWin} gameNumber={1} onRedo={() => setGameStarted(false)} onExit={onClose} />}
    </div>
  );
};

// GAME 2: LAUREL - Rhythm Tap (EASIER: Bigger lanes, slower notes, 20 hits to win)
const LaurelGame = ({ onClose }) => {
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [notes, setNotes] = useState([]);
  const [timeLeft, setTimeLeft] = useState(45);
  const [misses, setMisses] = useState(0);
  const { isMuted, toggleMute } = useGameAudio(gameAudioMap.laurel, gameStarted && !gameOver);

  const startGame = (name) => {
    setPlayerName(name);
    setGameStarted(true);
    setScore(0);
    setMisses(0);
    setTimeLeft(45);
    setNotes([]);
    setGameOver(false);
  };

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          setGameOver(true);
          setIsWin(score >= 20);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameStarted, gameOver, score]);

  // Check win condition
  useEffect(() => {
    if (score >= 20 && !gameOver) {
      setGameOver(true);
      setIsWin(true);
    }
    // Lose if too many misses
    if (misses >= 10 && !gameOver) {
      setGameOver(true);
      setIsWin(false);
    }
  }, [score, misses, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const lanes = [0, 1, 2, 3];
    const spawnNote = () => {
      const lane = lanes[Math.floor(Math.random() * lanes.length)];
      setNotes(prev => [...prev, { id: Date.now(), lane, y: 0 }]);
    };
    const interval = setInterval(spawnNote, 1000); // Slower spawn
    return () => clearInterval(interval);
  }, [gameStarted, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const move = setInterval(() => {
      setNotes(prev => {
        const updated = prev.map(n => ({ ...n, y: n.y + 3 })); // Slower fall
        const missed = updated.filter(n => n.y > 320);
        if (missed.length > 0) setMisses(m => m + missed.length);
        return updated.filter(n => n.y <= 320);
      });
    }, 30);
    return () => clearInterval(move);
  }, [gameStarted, gameOver]);

  const hitLane = (lane) => {
    const hitZone = notes.find(n => n.lane === lane && n.y > 220 && n.y < 320);
    if (hitZone) {
      setNotes(prev => prev.filter(n => n.id !== hitZone.id));
      setScore(s => s + 1);
    }
  };

  const laneColors = ['from-red-500 to-red-700', 'from-blue-500 to-blue-700', 'from-green-500 to-green-700', 'from-yellow-500 to-yellow-700'];

  if (!gameStarted) {
    return (
      <div className="text-center p-4">
        <h3 className="text-2xl font-heading text-white mb-2">LAUREL</h3>
        <p className="text-white/60 text-sm mb-4">Hit 20 notes to WIN! Miss 10 and you LOSE!</p>
        <div className="flex flex-wrap gap-2 justify-center max-h-28 overflow-y-auto mb-4">
          {[...heroMales, ...heroFemales].map(name => (
            <button key={name} onClick={() => startGame(name)} className="px-3 py-2 bg-green-600/50 hover:bg-green-500 text-white text-sm rounded-lg transition-colors font-medium">{name}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full p-3">
      <div className="flex justify-between items-center mb-2">
        <div className="text-white font-mono text-sm">Hits: {score}/20 | Misses: {misses}/10 | ⏱️ {timeLeft}s</div>
        <button onClick={toggleMute} className="text-white/60 hover:text-white p-1">
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="relative h-80 bg-black/50 rounded-lg overflow-hidden flex">
        {[0, 1, 2, 3].map(lane => (
          <div key={lane} className="flex-1 border-x border-white/10 relative">
            {notes.filter(n => n.lane === lane).map(note => (
              <div
                key={note.id}
                className={`absolute left-1 right-1 h-8 bg-gradient-to-b ${laneColors[lane]} rounded-lg shadow-lg`}
                style={{ top: note.y }}
              />
            ))}
            <button
              onClick={() => hitLane(lane)}
              className={`absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t ${laneColors[lane]} opacity-40 hover:opacity-80 active:opacity-100 transition-opacity border-t-4 border-white/50`}
            />
          </div>
        ))}
        <div className="absolute bottom-24 left-0 right-0 h-1 bg-white/60" />
      </div>
      <p className="text-center text-white/50 text-xs mt-2">TAP THE LANES WHEN NOTES REACH THE LINE</p>
      {gameOver && <GameResultModal isWin={isWin} gameNumber={2} onRedo={() => setGameStarted(false)} onExit={onClose} />}
    </div>
  );
};

// GAME 3: ANNAPOLIS - Flappy Bird (EASIER: Bigger gaps, slower speed, 8 to win)
const AnnapolisGame = ({ onClose }) => {
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [birdY, setBirdY] = useState(150);
  const [velocity, setVelocity] = useState(0);
  const [pipes, setPipes] = useState([]);
  const gameLoopRef = useRef(null);
  const { isMuted, toggleMute } = useGameAudio(gameAudioMap.annapolis, gameStarted && !gameOver);

  const startGame = (name) => {
    setPlayerName(name);
    setGameStarted(true);
    setScore(0);
    setBirdY(150);
    setVelocity(0);
    setPipes([]);
    setGameOver(false);
  };

  const flap = useCallback(() => {
    if (gameOver) return;
    setVelocity(-6); // Gentler flap
  }, [gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const handleKey = (e) => { if (e.code === 'Space') { e.preventDefault(); flap(); }};
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [gameStarted, gameOver, flap]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const spawnPipe = () => {
      const gapY = Math.random() * 100 + 80; // Gap position
      setPipes(prev => [...prev, { id: Date.now(), x: 320, gapY, gapSize: 120, passed: false }]); // Bigger gap
    };
    const interval = setInterval(spawnPipe, 2500); // Slower pipe spawn
    return () => clearInterval(interval);
  }, [gameStarted, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    
    gameLoopRef.current = setInterval(() => {
      setVelocity(v => Math.min(v + 0.35, 8)); // Gentler gravity
      setBirdY(y => {
        const newY = y + velocity;
        if (newY < 0 || newY > 300) {
          setGameOver(true);
          setIsWin(score >= 8);
        }
        return Math.max(0, Math.min(newY, 300));
      });
      
      setPipes(prev => {
        const updated = prev.map(p => ({ ...p, x: p.x - 2 })); // Slower pipes
        
        updated.forEach(pipe => {
          // Collision detection with bigger margin for fairness
          if (pipe.x > 25 && pipe.x < 55) {
            if (birdY < pipe.gapY - 10 || birdY > pipe.gapY + pipe.gapSize - 20) {
              setGameOver(true);
              setIsWin(score >= 8);
            }
          }
          // Score when passing
          if (pipe.x < 35 && !pipe.passed) {
            pipe.passed = true;
            setScore(s => {
              const newScore = s + 1;
              if (newScore >= 8) {
                setGameOver(true);
                setIsWin(true);
              }
              return newScore;
            });
          }
        });
        
        return updated.filter(p => p.x > -60);
      });
    }, 30);
    
    return () => clearInterval(gameLoopRef.current);
  }, [gameStarted, gameOver, velocity, birdY, score]);

  if (!gameStarted) {
    return (
      <div className="text-center p-4">
        <h3 className="text-2xl font-heading text-white mb-2">ANNAPOLIS</h3>
        <p className="text-white/60 text-sm mb-4">Pass through 8 pipes to WIN!</p>
        <div className="flex flex-wrap gap-2 justify-center max-h-28 overflow-y-auto mb-4">
          {[...heroMales, ...heroFemales].map(name => (
            <button key={name} onClick={() => startGame(name)} className="px-3 py-2 bg-cyan-600/50 hover:bg-cyan-500 text-white text-sm rounded-lg transition-colors font-medium">{name}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full p-3" onClick={flap}>
      <div className="flex justify-between items-center mb-2">
        <div className="text-white font-mono text-sm">Pipes: {score}/8</div>
        <button onClick={(e) => { e.stopPropagation(); toggleMute(); }} className="text-white/60 hover:text-white p-1">
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="relative h-80 bg-gradient-to-b from-cyan-900/30 to-blue-900/50 rounded-lg overflow-hidden cursor-pointer">
        {/* Bird */}
        <div 
          className="absolute w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full shadow-lg flex items-center justify-center text-xs font-bold text-white border-2 border-white/50"
          style={{ left: 40, top: birdY, transform: `rotate(${velocity * 2}deg)` }}
        >
          {playerName.slice(0,2)}
        </div>
        
        {/* Pipes */}
        {pipes.map(pipe => (
          <React.Fragment key={pipe.id}>
            <div 
              className="absolute w-14 bg-gradient-to-r from-green-500 to-green-600 rounded-b-lg border-2 border-green-400"
              style={{ left: pipe.x, top: 0, height: pipe.gapY }}
            />
            <div 
              className="absolute w-14 bg-gradient-to-r from-green-500 to-green-600 rounded-t-lg border-2 border-green-400"
              style={{ left: pipe.x, top: pipe.gapY + pipe.gapSize, bottom: 0 }}
            />
          </React.Fragment>
        ))}
        
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm font-bold">TAP OR SPACE TO FLY</p>
      </div>
      {gameOver && <GameResultModal isWin={isWin} gameNumber={3} onRedo={() => setGameStarted(false)} onExit={onClose} />}
    </div>
  );
};

// GAME 4: ISABEL - Endless Runner (EASIER: Bigger player, more time to react, 30 to win)
const IsabelGame = ({ onClose }) => {
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [playerY, setPlayerY] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [isJumping, setIsJumping] = useState(false);
  const { isMuted, toggleMute } = useGameAudio(gameAudioMap.isabel, gameStarted && !gameOver);

  const startGame = (name) => {
    setPlayerName(name);
    setGameStarted(true);
    setScore(0);
    setPlayerY(0);
    setObstacles([]);
    setGameOver(false);
    setIsJumping(false);
  };

  const jump = useCallback(() => {
    if (isJumping || gameOver) return;
    setIsJumping(true);
    setPlayerY(100); // Higher jump
    setTimeout(() => {
      setPlayerY(0);
      setIsJumping(false);
    }, 500);
  }, [isJumping, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const handleKey = (e) => { if (e.code === 'Space') { e.preventDefault(); jump(); }};
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [gameStarted, gameOver, jump]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const spawnObstacle = () => {
      const villain = villains[Math.floor(Math.random() * villains.length)];
      setObstacles(prev => [...prev, { id: Date.now(), x: 320, name: villain, height: 35 }]);
    };
    const interval = setInterval(spawnObstacle, 1800); // Slower spawn
    return () => clearInterval(interval);
  }, [gameStarted, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    const gameLoop = setInterval(() => {
      setObstacles(prev => {
        const updated = prev.map(o => ({ ...o, x: o.x - 4 })); // Slower obstacles
        
        updated.forEach(o => {
          // More forgiving collision
          if (o.x > 40 && o.x < 80 && playerY < o.height - 15) {
            setGameOver(true);
            setIsWin(score >= 30);
          }
        });
        
        return updated.filter(o => o.x > -50);
      });
      
      setScore(s => {
        const newScore = s + 1;
        if (newScore >= 30) {
          setGameOver(true);
          setIsWin(true);
        }
        return newScore;
      });
    }, 60);
    return () => clearInterval(gameLoop);
  }, [gameStarted, gameOver, playerY, score]);

  if (!gameStarted) {
    return (
      <div className="text-center p-4">
        <h3 className="text-2xl font-heading text-white mb-2">ISABEL</h3>
        <p className="text-white/60 text-sm mb-4">Run 30 meters to WIN! Jump over villains!</p>
        <div className="flex flex-wrap gap-2 justify-center max-h-28 overflow-y-auto mb-4">
          {[...heroMales, ...heroFemales].map(name => (
            <button key={name} onClick={() => startGame(name)} className="px-3 py-2 bg-orange-600/50 hover:bg-orange-500 text-white text-sm rounded-lg transition-colors font-medium">{name}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full p-3" onClick={jump}>
      <div className="flex justify-between items-center mb-2">
        <div className="text-white font-mono text-sm">Distance: {score}/30m</div>
        <button onClick={(e) => { e.stopPropagation(); toggleMute(); }} className="text-white/60 hover:text-white p-1">
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="relative h-80 bg-gradient-to-r from-orange-900/30 to-red-900/30 rounded-lg overflow-hidden cursor-pointer">
        {/* Ground */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-amber-900 to-amber-700" />
        
        {/* Player */}
        <div 
          className="absolute w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-xl shadow-lg flex items-center justify-center text-sm font-bold text-white border-2 border-white/50 transition-all duration-200"
          style={{ left: 50, bottom: 16 + playerY }}
        >
          {playerName.slice(0,3)}
        </div>
        
        {/* Obstacles */}
        {obstacles.map(o => (
          <div 
            key={o.id}
            className="absolute bg-gradient-to-br from-red-500 to-red-700 rounded flex items-center justify-center text-[8px] font-bold text-white border border-red-400"
            style={{ left: o.x, bottom: 16, width: 35, height: o.height }}
          >
            {o.name.slice(0,3)}
          </div>
        ))}
        
        <p className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/50 text-sm font-bold">TAP OR SPACE TO JUMP</p>
      </div>
      {gameOver && <GameResultModal isWin={isWin} gameNumber={4} onRedo={() => setGameStarted(false)} onExit={onClose} />}
    </div>
  );
};

// GAME 5: QUIVIRA - Snake (EASIER: Slower, 8 food to win)
const QuiviraGame = ({ onClose }) => {
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isWin, setIsWin] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [snake, setSnake] = useState([{ x: 6, y: 6 }]);
  const [food, setFood] = useState({ x: 10, y: 10 });
  const [direction, setDirection] = useState({ x: 1, y: 0 });
  const directionRef = useRef({ x: 1, y: 0 });
  const { isMuted, toggleMute } = useGameAudio(gameAudioMap.quivira, gameStarted && !gameOver);

  const gridSize = 12;

  const startGame = (name) => {
    setPlayerName(name);
    setGameStarted(true);
    setScore(0);
    setSnake([{ x: 6, y: 6 }]);
    setFood({ x: 10, y: 10 });
    setDirection({ x: 1, y: 0 });
    directionRef.current = { x: 1, y: 0 };
    setGameOver(false);
  };

  const spawnFood = useCallback(() => {
    setFood({
      x: Math.floor(Math.random() * gridSize),
      y: Math.floor(Math.random() * gridSize)
    });
  }, []);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    
    const handleKey = (e) => {
      const current = directionRef.current;
      switch(e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          if (current.y !== 1) {
            directionRef.current = { x: 0, y: -1 };
            setDirection({ x: 0, y: -1 });
          }
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          if (current.y !== -1) {
            directionRef.current = { x: 0, y: 1 };
            setDirection({ x: 0, y: 1 });
          }
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          if (current.x !== 1) {
            directionRef.current = { x: -1, y: 0 };
            setDirection({ x: -1, y: 0 });
          }
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          if (current.x !== -1) {
            directionRef.current = { x: 1, y: 0 };
            setDirection({ x: 1, y: 0 });
          }
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [gameStarted, gameOver]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    
    const moveSnake = setInterval(() => {
      setSnake(prev => {
        const head = prev[0];
        const dir = directionRef.current;
        const newHead = {
          x: (head.x + dir.x + gridSize) % gridSize,
          y: (head.y + dir.y + gridSize) % gridSize
        };
        
        // Check self collision (skip first few segments for fairness)
        if (prev.slice(2).some(seg => seg.x === newHead.x && seg.y === newHead.y)) {
          setGameOver(true);
          setIsWin(score >= 8);
          return prev;
        }
        
        const newSnake = [newHead, ...prev];
        
        // Check food
        if (newHead.x === food.x && newHead.y === food.y) {
          setScore(s => {
            const newScore = s + 1;
            if (newScore >= 8) {
              setGameOver(true);
              setIsWin(true);
            }
            return newScore;
          });
          spawnFood();
        } else {
          newSnake.pop();
        }
        
        return newSnake;
      });
    }, 200); // Slower snake
    
    return () => clearInterval(moveSnake);
  }, [gameStarted, gameOver, food, score, spawnFood]);

  const handleTouch = (dir) => {
    const current = directionRef.current;
    if (dir === 'up' && current.y !== 1) {
      directionRef.current = { x: 0, y: -1 };
      setDirection({ x: 0, y: -1 });
    }
    if (dir === 'down' && current.y !== -1) {
      directionRef.current = { x: 0, y: 1 };
      setDirection({ x: 0, y: 1 });
    }
    if (dir === 'left' && current.x !== 1) {
      directionRef.current = { x: -1, y: 0 };
      setDirection({ x: -1, y: 0 });
    }
    if (dir === 'right' && current.x !== -1) {
      directionRef.current = { x: 1, y: 0 };
      setDirection({ x: 1, y: 0 });
    }
  };

  if (!gameStarted) {
    return (
      <div className="text-center p-4">
        <h3 className="text-2xl font-heading text-white mb-2">QUIVIRA</h3>
        <p className="text-white/60 text-sm mb-4">Eat 8 food to WIN! Don't hit yourself!</p>
        <div className="flex flex-wrap gap-2 justify-center max-h-28 overflow-y-auto mb-4">
          {[...heroMales, ...heroFemales].map(name => (
            <button key={name} onClick={() => startGame(name)} className="px-3 py-2 bg-teal-600/50 hover:bg-teal-500 text-white text-sm rounded-lg transition-colors font-medium">{name}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full p-3">
      <div className="flex justify-between items-center mb-2">
        <div className="text-white font-mono text-sm">Food: {score}/8</div>
        <button onClick={toggleMute} className="text-white/60 hover:text-white p-1">
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="relative bg-black/50 rounded-lg overflow-hidden mx-auto" style={{ width: '240px', height: '240px' }}>
        <div className="grid absolute inset-0" style={{ 
          display: 'grid', 
          gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
          gridTemplateRows: `repeat(${gridSize}, 1fr)`,
          gap: '1px'
        }}>
          {Array.from({ length: gridSize * gridSize }).map((_, i) => {
            const x = i % gridSize;
            const y = Math.floor(i / gridSize);
            const isSnake = snake.some(seg => seg.x === x && seg.y === y);
            const isHead = snake[0].x === x && snake[0].y === y;
            const isFood = food.x === x && food.y === y;
            
            return (
              <div
                key={i}
                className={`${
                  isHead ? 'bg-gradient-to-br from-green-400 to-emerald-600 rounded' :
                  isSnake ? 'bg-gradient-to-br from-green-600 to-green-800' :
                  isFood ? 'bg-gradient-to-br from-red-400 to-pink-500 rounded-full animate-pulse' :
                  'bg-white/5'
                }`}
              />
            );
          })}
        </div>
      </div>
      {/* Touch controls - bigger buttons */}
      <div className="flex justify-center gap-2 mt-3">
        <button onClick={() => handleTouch('left')} className="w-12 h-12 bg-white/20 hover:bg-white/40 active:bg-white/60 rounded-lg text-white text-xl font-bold">←</button>
        <div className="flex flex-col gap-2">
          <button onClick={() => handleTouch('up')} className="w-12 h-12 bg-white/20 hover:bg-white/40 active:bg-white/60 rounded-lg text-white text-xl font-bold">↑</button>
          <button onClick={() => handleTouch('down')} className="w-12 h-12 bg-white/20 hover:bg-white/40 active:bg-white/60 rounded-lg text-white text-xl font-bold">↓</button>
        </div>
        <button onClick={() => handleTouch('right')} className="w-12 h-12 bg-white/20 hover:bg-white/40 active:bg-white/60 rounded-lg text-white text-xl font-bold">→</button>
      </div>
      {gameOver && <GameResultModal isWin={isWin} gameNumber={5} onRedo={() => setGameStarted(false)} onExit={onClose} />}
    </div>
  );
};

// Main Games Component
const Games = () => {
  const [activeGame, setActiveGame] = useState(null);
  const { setRippleEnabled } = useRipple();

  // Disable ripple effect when game is active
  useEffect(() => {
    setRippleEnabled(activeGame === null);
    return () => setRippleEnabled(true);
  }, [activeGame, setRippleEnabled]);

  const games = [
    { id: 'corbett', name: 'CORBETT', type: 'Memory Match', gradient: 'from-purple-900/40 to-blue-900/40', btnGradient: 'from-purple-500 to-blue-500', track: 'MYSTERY BOX I', emoji: '🧠' },
    { id: 'laurel', name: 'LAUREL', type: 'Rhythm Tap', gradient: 'from-green-900/40 to-teal-900/40', btnGradient: 'from-green-500 to-teal-500', track: 'MYSTERY BOX II', emoji: '🎵' },
    { id: 'annapolis', name: 'ANNAPOLIS', type: 'Flappy Bird', gradient: 'from-cyan-900/40 to-blue-900/40', btnGradient: 'from-cyan-500 to-blue-500', track: 'MYSTERY BOX III', emoji: '🐦' },
    { id: 'isabel', name: 'ISABEL', type: 'Endless Runner', gradient: 'from-orange-900/40 to-red-900/40', btnGradient: 'from-orange-500 to-red-500', track: 'MYSTERY BOX IV', emoji: '🏃' },
    { id: 'quivira', name: 'QUIVIRA', type: 'Snake', gradient: 'from-teal-900/40 to-emerald-900/40', btnGradient: 'from-teal-500 to-emerald-500', track: 'MYSTERY BOX V', emoji: '🐍' },
  ];

  const GameComponent = {
    corbett: CorbettGame,
    laurel: LaurelGame,
    annapolis: AnnapolisGame,
    isabel: IsabelGame,
    quivira: QuiviraGame,
  };

  return (
    <section className="py-20 px-4" data-testid="games-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-center font-heading text-4xl md:text-5xl text-white mb-4 tracking-wider">
          PLAY TO UNLOCK SECRETS
        </h2>
        <p className="text-center font-mono text-sm text-white/50 mb-12">
          Win all 5 games to unlock exclusive SoundCloud content • Each game plays a MYSTERY BOX track
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {games.map((game) => {
            const ActiveGameComponent = GameComponent[game.id];
            return (
              <div key={game.id} className={`relative bg-gradient-to-br ${game.gradient} border border-white/10 rounded-2xl overflow-hidden min-h-[480px]`}>
                {activeGame === game.id ? (
                  <>
                    <button onClick={() => setActiveGame(null)} className="absolute top-2 right-2 z-50 text-white/50 hover:text-white bg-black/50 rounded-full p-1">
                      <X className="w-6 h-6" />
                    </button>
                    <ActiveGameComponent onClose={() => setActiveGame(null)} />
                  </>
                ) : (
                  <div className="p-6 text-center h-full flex flex-col justify-center">
                    <div className="text-5xl mb-4">{game.emoji}</div>
                    <h3 className="font-heading text-2xl text-white mb-1">{game.name}</h3>
                    <p className="font-mono text-xs text-white/50 mb-2">{game.type}</p>
                    <p className="font-mono text-[10px] text-purple-400 mb-6">♫ {game.track}</p>
                    <button 
                      onClick={() => setActiveGame(game.id)}
                      className={`px-8 py-4 bg-gradient-to-r ${game.btnGradient} text-white font-bold text-lg rounded-xl hover:scale-105 transition-transform border border-white/20 shadow-lg`}
                    >
                      PLAY
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Games;
